/**
 * Shared module for named records with an attached file: CRM documents and contracts.
 * Everyone can browse and open files; upload, replace and delete are Admin-only
 * (enforced by the hub policy `global::is-admin` as well).
 */
import React, { useRef, useState } from "react";
import { Loader2, Plus } from "lucide-react";

import {
    MainLayout,
    AppButton,
    SearchInput,
    ListToolbar,
    Pagination,
    LoadingState,
    ErrorBanner,
    MessageModal,
    MessageState,
    ConfirmModal,
    FileRecordsView,
    FileRecordRow,
    useDebouncedValue,
} from "../ui/components";
import { usePaginatedList } from "../../api/usePaginatedList";
import { createEntry, deleteEntry, mediaUrl, toUserMessage, updateEntry, uploadFile } from "../../api/strapi";
import { FileRecord, UserProfile } from "../../types/objects";
import { isAdmin as isAdminRole } from "../../types/roles";

export interface FileRecordsConfig {
    resource: "crmdocuments" | "contracts";
    title: string;
    subtitle: string;
    /** Czech noun in accusative for messages, e.g. "dokument", "smlouvu". */
    nounAccusative: string;
    emptyLabel: string;
}

/** Maximum upload size accepted in the UI (the server may enforce its own limit). */
export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export default function FileRecordsPane({ user, config }: { user: UserProfile; config: FileRecordsConfig }) {
    const isAdmin = isAdminRole(user.role?.name);
    const [searchTerm, setSearchTerm] = useState("");
    const search = useDebouncedValue(searchTerm);
    const list = usePaginatedList<FileRecord>(config.resource, { search });

    const fileInputRef = useRef<HTMLInputElement>(null);
    const [replaceTarget, setReplaceTarget] = useState<FileRecordRow | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<FileRecordRow | null>(null);
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState<MessageState | null>(null);

    const rows: FileRecordRow[] = list.rows.map((r) => ({
        id: r.id,
        documentId: r.documentId,
        name: r.name || "Bez názvu",
        fileCount: r.content?.length ?? 0,
        createdAt: r.createdAt,
    }));

    const pickFile = (target: FileRecordRow | null) => {
        setReplaceTarget(target);
        fileInputRef.current?.click();
    };

    const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file) return;
        if (file.size > MAX_UPLOAD_BYTES) {
            setMessage({ type: "error", title: "Soubor je příliš velký", text: "Maximální velikost souboru je 50 MB." });
            return;
        }
        setUploading(true);
        try {
            const fileId = await uploadFile(file);
            if (replaceTarget) {
                await updateEntry(config.resource, replaceTarget.documentId, { content: [fileId] });
            } else {
                await createEntry(config.resource, { name: file.name, content: [fileId] });
            }
            setMessage({ type: "success", title: "Hotovo", text: `Soubor „${file.name}“ byl úspěšně nahrán.` });
            await list.reload();
        } catch (err) {
            console.error(`[${config.resource}] Upload failed`, err);
            setMessage({ type: "error", title: "Chyba nahrávání", text: toUserMessage(err, "Soubor se nepodařilo nahrát na server.") });
        } finally {
            setUploading(false);
            setReplaceTarget(null);
        }
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setBusy(true);
        try {
            await deleteEntry(config.resource, deleteTarget.documentId);
            setDeleteTarget(null);
            await list.reload();
        } catch (err) {
            console.error(`[${config.resource}] Delete failed`, err);
            setDeleteTarget(null);
            setMessage({ type: "error", title: "Chyba při mazání", text: toUserMessage(err, `${config.nounAccusative} se nepodařilo smazat.`) });
        } finally {
            setBusy(false);
        }
    };

    const openFile = (row: FileRecordRow) => {
        const record = list.rows.find((r) => r.documentId === row.documentId);
        const url = mediaUrl(record?.content?.[0]);
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            setMessage({ type: "error", title: "Soubor nenalezen", text: "K záznamu není přiložen žádný soubor." });
        }
    };

    return (
        <MainLayout
            title={config.title}
            subtitle={config.subtitle}
            actions={isAdmin && (
                <AppButton variant="success" onClick={() => pickFile(null)} disabled={uploading}>
                    <Plus size={18} strokeWidth={3} /> Nahrát soubor
                </AppButton>
            )}
        >
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} data-testid="file-input" />

            <ListToolbar total={list.pagination.total} pageSize={list.pageSize} loading={list.loading} onRefresh={list.reload} onPageSizeChange={list.setPageSize}>
                <SearchInput value={searchTerm} onChange={setSearchTerm} placeholder="Hledat podle názvu…" />
            </ListToolbar>

            <div className="space-y-6">
                {uploading && (
                    <div className="flex items-center justify-center gap-3 text-green-600 font-bold py-4 bg-green-50 rounded-2xl border border-green-100 uppercase text-xs" role="status">
                        <Loader2 className="animate-spin" size={20} />
                        {replaceTarget ? "Nahrazuji soubor…" : "Nahrávám soubor…"}
                    </div>
                )}
                {list.error && <ErrorBanner message={list.error} onRetry={list.reload} />}
                {list.loading && list.rows.length === 0 ? (
                    <LoadingState />
                ) : (
                    <FileRecordsView
                        rows={rows}
                        isAdmin={isAdmin}
                        emptyLabel={search ? "Hledání neodpovídá žádný záznam." : config.emptyLabel}
                        onOpen={openFile}
                        onReplace={(row) => pickFile(row)}
                        onDelete={setDeleteTarget}
                    />
                )}
                <Pagination page={list.page} pageCount={list.pagination.pageCount} total={list.pagination.total} onPageChange={list.setPage} />
            </div>

            <ConfirmModal
                open={!!deleteTarget}
                title="Smazat záznam?"
                text={<>Záznam <strong>„{deleteTarget?.name}“</strong> bude trvale odstraněn.</>}
                busy={busy}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteTarget(null)}
            />
            <MessageModal message={message} onClose={() => setMessage(null)} />
        </MainLayout>
    );
}
